<navbar>

    <div class="w-full flex flex-row justify-between items-center mb-4" if="{state.isAuthenticated && locale}">
        <div class="flex flex-col items-start">
            <img if="{!state.isPartnerAdmin && state.customerLogoUrl && state.customerLogoUrl.length > 0}" src="{tools?.appendCacheBusterParam(state.customerLogoUrl)}" alt="{locale?.t('navbar_customer_logo_alt', { name: state.customerName })}" class="h-8 w-auto mb-1" onerror="{handleLogoError}"  />
            <span if="{!state.isPartnerAdmin && (!state.customerLogoUrl || state.customerLogoUrl.length === 0)}" class="text-2xl">{state.customerName}</span>
            <!-- METALARK-929: a PARTNER_ADMIN before entering a customer has no
                 customer context, so show the partner name (its portal identity)
                 in place of the customer name/logo. Once entered, the role
                 downgrades to CUSTOMER_ADMIN and the customer context shows instead. -->
            <span if="{state.isPartnerAdmin && state.partnerName}" class="text-2xl">{state.partnerName}</span>
            <!--
                During any active access session (impersonation or workspace-access)
                the header shows the workspace name in place of user/role. User
                identity moves into the settings-cog dropdown (see menu below).
                For impersonation the red banner elsewhere provides the heavy
                visual cue; workspace-access has no banner, so this swap is the
                primary signal that the admin is operating inside a workspace.
            -->
            <span if="{hasActiveAccessSession() && state.workspaceName}" class="text-sm">
                <span class="font-bold text-accent">{locale?.t('nav_workspace_label')}: {state.workspaceName}</span>
            </span>
            <span if="{!hasActiveAccessSession()}" class="text-sm">
                <span class="font-bold text-accent">{state.userName ? (locale?.t('nav_welcome') + state.userName) : ''}</span>
                <span class="">{state.userRole ? (' | ' + getDisplayRole()) : ''}</span>
            </span>
        </div>

        <!-- Main menu - only show when authenticated -->
        <div if="{state.isAuthenticated}" class="md:flex flex-row main-menu">

            <!-- Mobile menu button - only visible on small screens -->
            <div class="md:hidden flex-shrink-0">
                <label for="nav-drawer" class="btn btn-ghost btn-square"
                       aria-label="{locale?.t('navbar_open_menu') || 'Open menu'}">
                    <i class="icon-menu text-2xl" aria-hidden="true"></i>
                </label>
            </div>

            <!-- Desktop menu - only visible on medium+ screens -->
            <div class="hidden md:flex flex-row flex-shrink-0 main-menu">
                <!--
                    Workspace-navigation cluster (home / upload / content /
                    skills / reports / export). Hidden for admin-tier callers
                    outside of an active access session UNLESS they hold
                    exactly one direct-role slice — in that case the menu is
                    visible and each click auto-creates a workspace-access
                    session for that single slice before navigating (see
                    handleWorkspaceNavClick). Admins with zero or 2+ direct-
                    role slices must pick a workspace from the Your Workspaces
                    card before the workspace menu becomes available.
                -->
                <virtual if="{shouldShowWorkspaceMenu()}">
                <!--
                    Wrapper animates the workspace cluster in from the right
                    when it transitions from hidden → visible (admin enters a
                    workspace session). The keyframe plays on every render of
                    this branch — including page reloads inside an active
                    session — but the effect is short and subtle enough to
                    stay pleasant rather than annoying. See <style scoped>
                    below for the keyframe definition.
                -->
                <div class="workspace-menu-cluster flex flex-row items-center">
                <div class="px-4 py-1 flex items-center">
                    <a href="#" onclick="{(e) => handleWorkspaceNavClick(e, '#home')}" class="uppercase text-sm items-center flex font-semibold hover:opacity-70 {isActive('') || isActive('home') ? 'text-accent' : ''}">
                        <i class="icon-metalark-logo me-2"></i>
                        <span>{locale?.t('nav_home')}</span>
                    </a>
                </div>
                <div class="px-3 py-1 flex items-center">
                    <a href="#upload" onclick="{(e) => handleWorkspaceNavClick(e, '#upload')}" class="uppercase text-sm items-center flex font-semibold hover:opacity-70 {isActive('upload') ? 'text-accent' : ''}">
                        <i class="icon-metalark-upload me-2"></i>
                        <span>{locale?.t('nav_upload')}</span></a>
                </div>
                <div class="px-3 py-1 flex items-center">
                    <a href="#content" onclick="{(e) => handleWorkspaceNavClick(e, '#content')}" class="uppercase text-sm items-center flex font-semibold hover:opacity-70 {isActive('content') ? 'text-accent' : ''}">
                        <i class="icon-metalark-edit-note me-2"></i>
                        <span>{locale?.t('nav_content')}</span></a>
                </div>
                <div class="px-3 py-1 flex items-center">
                    <a href="#skills" onclick="{(e) => handleWorkspaceNavClick(e, '#skills')}" class="uppercase text-sm items-center flex font-semibold hover:opacity-70 {isActive('skills') ? 'text-accent' : ''}">
                        <i class="icon-metalark-skills me-2"></i>
                        <span>{locale?.t('nav_skills')}</span></a>
                </div>
                <div class="px-3 py-1 flex items-center">
                    <a href="#reports" onclick="{(e) => handleWorkspaceNavClick(e, '#reports')}" class="uppercase text-sm items-center flex font-semibold hover:opacity-70 {isActive('reports') ? 'text-accent' : ''}">
                        <i class="icon-metalark-chart me-2"></i>
                        <span>{locale?.t('nav_reports')}</span>
                    </a>
                </div>
                <div class="ps-3 pe-8 py-1 flex items-center">
                    <a href="#export" onclick="{(e) => handleWorkspaceNavClick(e, '#export')}" class="uppercase text-sm items-center flex font-semibold hover:opacity-70 {isActive('export') ? 'text-accent' : ''}">
                        <i class="icon-metalark-export me-2"></i>
                        <span>{locale?.t('nav_metadata')}</span>
                    </a>
                </div>
                </div><!-- /.workspace-menu-cluster -->
                </virtual>

                <!-- METALARK-929: partner portal nav. A PARTNER_ADMIN (before
                     entering a customer) has no workspace cluster — it administers
                     customers by entering them — so surface its portal links here,
                     where the workspace cluster would otherwise sit empty. Once it
                     enters a customer the role downgrades to CUSTOMER_ADMIN, this
                     hides, and the normal workspace menu takes over. -->
                <virtual if="{state.isPartnerAdmin}">
                <div class="flex flex-row items-center">
                    <div class="px-4 py-1 flex items-center">
                        <a href="#partner-dashboard" onclick="{handleNavClick}" class="uppercase text-sm items-center flex font-semibold hover:opacity-70 {isActive('partner-dashboard') ? 'text-accent' : ''}">
                            <i class="icon-metalark-logo me-2"></i>
                            <span>{locale?.t('partner_nav_dashboard')}</span>
                        </a>
                    </div>
                    <div class="px-3 py-1 flex items-center">
                        <a href="#partner-customers" onclick="{handleNavClick}" class="uppercase text-sm items-center flex font-semibold hover:opacity-70 {(isActive('partner-customers') || isActive('customer-360')) ? 'text-accent' : ''}">
                            <i class="icon-business me-2"></i>
                            <span>{locale?.t('partner_nav_customers')}</span>
                        </a>
                    </div>
                    <div class="ps-3 pe-8 py-1 flex items-center">
                        <a href="#webhook-subscriptions" onclick="{handleNavClick}" class="uppercase text-sm items-center flex font-semibold hover:opacity-70 {isActive('webhook-subscriptions') ? 'text-accent' : ''}">
                            <i class="icon-bell me-2"></i>
                            <span>{locale?.t('partner_nav_webhooks')}</span>
                        </a>
                    </div>
                </div>
                </virtual>

                <details id="gear-menu" class="py-1 ps-8 flex border-s-1 border-base-300 items-center dropdown dropdown-bottom dropdown-end">
                    <summary id="menu-button"
                             aria-label="{locale?.t('nav_settings')}"
                             class="uppercase text-sm items-center flex btn-ghost hover:opacity-70 cursor-pointer list-none relative">
                        <i class="icon-metalark-settings me-0" aria-hidden="true"></i>
                        <!--
                            METALARK-835: small accent dot when the user
                            has pending invitations addressed to them.
                            Kept a dot (not a count) on the cog to avoid
                            visual noise alongside the existing settings
                            icon; the exact count lives one click in,
                            on the dropdown entry itself.
                        -->
                        <span if="{state.pendingInvitationCount > 0}"
                            class="absolute top-0 right-0 w-2 h-2 bg-accent rounded-full"
                            aria-label="{locale?.t('navbar_pending_invitations_aria')}"></span>
                    </summary>
                    <ul class="menu dropdown-content bg-white rounded-lg z-[1] shadow-md w-max mt-5 border border-base-200">
                        <!--
                            User identity header. Always visible so admins can
                            see who they are logged in as, since the top-left
                            header area is taken over by the customer/workspace
                            label during an active access session. Non-clickable —
                            just a label row.
                        -->
                        <li class="px-2 pt-2 pb-1 pointer-events-none">
                            <div class="flex flex-col">
                                <span class="text-sm font-bold text-accent whitespace-nowrap">{state.userName}</span>
                                <span class="text-xs opacity-80 whitespace-nowrap">{state.userRole ? getDisplayRole() : ''}</span>
                            </div>
                        </li>
                        <li class="bg-transparent mb-2">
                            <language-selector compact="true" class="flex flex-row"></language-selector>
                        </li>
                        <!--
                            Session-exit action. Always shown for IMPERSONATION
                            (admin needs to drop the downgraded session role to
                            resume admin tasks). For WORKSPACE_ACCESS, only
                            shown when the caller has 2+ direct workspaces —
                            single-workspace callers have nowhere to switch to
                            and their primary admin nav (Admin Dashboard,
                            Billing, etc.) remains reachable in this same
                            dropdown without exiting the session, so an
                            "Exit Workspace" entry there only logged them out
                            of their own workspace and stranded them on home.
                            The IMPERSONATION case still has the red banner
                            with its own exit button; the dropdown entry is
                            offered for parity. Both kinds funnel through
                            handleExitAccessSession so the end-session call,
                            Redux clear, and navigation home are consistent.
                        -->
                        <li if="{state.isImpersonating || (state.isWorkspaceAccess && state.directRoleCount > 1)}">
                            <a href="javascript:" onclick="{handleExitAccessSession}"
                               class="mb-2 mt-2 uppercase whitespace-nowrap text-sm bg-white hover:opacity-70">
                                <i class="icon-metalark-logout me-2"></i>
                                {state.isWorkspaceAccess ? locale?.t('nav_exit_workspace_session') : locale?.t('nav_return_to_admin')}
                            </a>
                        </li>
                        <!--
                            Workspace switcher for admin-tier callers with
                            two or more direct workspace roles. Single-
                            direct-role admins enter their lone workspace
                            via the inline workspace-menu cluster (handled
                            elsewhere); zero-direct-role admins reach
                            workspaces only through Customer 360 →
                            impersonation. This entry is the multi-
                            workspace case — clicking it routes to the
                            picker so the admin can switch without going
                            back through Customer 360.
                        -->
                        <li if="{state.directRoleCount > 1}">
                            <a href="#/select-workspace" onclick="{handleNavClick}"
                               class="mb-2 uppercase whitespace-nowrap text-sm bg-white hover:opacity-70 {isActive('select-workspace') ? 'text-accent font-bold' : ''}">
                                <i class="icon-metalark-logo me-2"></i> {locale?.t('nav_selectworkspace')}
                            </a>
                        </li>
                        <!--
                            Visual separator between the workspace switcher
                            and the rest of the admin / settings entries. Only
                            rendered when the switcher itself is rendered
                            (directRoleCount > 1) so it doesn't leave a
                            floating divider for single- or zero-direct-role
                            admins. Tightened margins so it reads as a
                            subdivider, not a section break.
                        -->
                        <li if="{state.directRoleCount > 1}" class="mt-1 pointer-events-none px-2 -my-1">
                            <hr class="border-t border-base-200 my-1" />
                        </li>
                        <li if="{state.isCustomerAdmin}"><a href="#admin" onclick="{handleNavClick}"
                               class="mb-2 uppercase whitespace-nowrap text-sm bg-white hover:opacity-70 {isAdminDashboardActive() ? 'text-accent font-bold' : ''}">
                            <i class="icon-metalark-settings me-2"></i> {locale?.t('nav_admindashboard')}</a>
                        </li>
                        <!-- METALARK-929: a PARTNER_ADMIN's cog entry routes to its
                             partner portal dashboard (it has no system admin dashboard).
                             Mobile-reachable counterpart to the main-nav partner cluster.
                             The label lives in the BASE locale (which partners load),
                             so it localizes correctly. -->
                        <li if="{state.isPartnerAdmin}"><a href="#partner-dashboard" onclick="{handleNavClick}"
                               class="mb-2 uppercase whitespace-nowrap text-sm bg-white hover:opacity-70 {(isActive('partner-dashboard') || isActive('partner-customers')) ? 'text-accent font-bold' : ''}">
                            <i class="icon-metalark-settings me-2"></i> {locale?.t('partner_nav_portal')}</a>
                        </li>
                        <li><a href="#support" onclick="{handleNavClick}"
                               class="mb-2 uppercase whitespace-nowrap text-sm bg-white hover:opacity-70 {isActive('support') ? 'text-accent' : ''}">
                            <i class="icon-contact_support me-2 text-lg"></i> {locale?.t('nav_support')}</a>
                        </li>
                        <li if="{state.isCustomerAdmin}"><a href="#/customer-360?tab=billing&view=usage" onclick="{handleNavClick}"
                               class="mb-2 uppercase text-sm bg-white hover:opacity-70 {isCreditsNavActive() ? 'text-accent font-bold' : ''}">
                            <i class="icon-metalark-list me-2"></i> {locale?.t('nav_processingcredits')}</a>
                        </li>
                        <li if="{state.isCustomerAdmin && state.featureFlags['billing_ui_page'] === true}">
                            <a href="#/customer-360?tab=billing&view=subscription" onclick="{handleNavClick}"
                               class="mb-2 uppercase whitespace-nowrap text-sm bg-white hover:opacity-70 {isBillingNavActive() ? 'text-accent' : ''}">
                                <i class="icon-metalark-cart me-2"></i> {locale?.t('nav_billing')}</a>
                        </li>
                        <!--
                            METALARK-835: pending-invitations entry. Only
                            rendered when there's something to act on so
                            the dropdown doesn't carry a permanent
                            "Invitations (0)" row. Count badge mirrors
                            DaisyUI's badge-sm pattern used elsewhere
                            (e.g. nav_processingcredits when over quota).
                        -->
                        <li if="{state.pendingInvitationCount > 0}">
                            <a href="#/invitations" onclick="{handleNavClick}"
                               class="mb-2 uppercase whitespace-nowrap text-sm bg-white hover:opacity-70 {isActive('invitations') ? 'text-accent font-bold' : ''}">
                                <i class="icon-metalark-mail mr-2"></i>
                                {locale?.t('nav_pending_invitations')}
                                <span class="badge badge-sm badge-accent ml-2">{state.pendingInvitationCount}</span>
                            </a>
                        </li>
                        <li><a href="#settings" onclick="{handleNavClick}"
                               class="mb-2 uppercase whitespace-nowrap text-sm bg-white hover:opacity-70 {isActive('settings') ? 'text-accent' : ''}">
                            <i class="icon-metalark-settings me-2"></i> {locale?.t('nav_settings')}</a>
                        </li>
                        <li class="pointer-events-none px-2 -my-1">
                            <hr class="border-t border-base-200 my-1" />
                        </li>
                        <li><a href="javascript:" onclick="{handleLogout}"
                               class="mb-2 uppercase whitespace-nowrap text-sm bg-white hover:opacity-70">
                            <i class="icon-metalark-logout me-2"></i> {locale?.t('nav_logout')}</a>
                        </li>
                    </ul>
                </details>
            </div>

            <!-- Mobile Drawer - only visible on mobile -->
            <div class="drawer drawer-end md:hidden" if="{state.isAuthenticated}">
                <input id="nav-drawer" type="checkbox" class="drawer-toggle" />
                <div class="drawer-side z-50">
                    <button type="button"
                            aria-label="{locale?.t('close')}"
                            class="drawer-overlay"
                            onclick="{closeMobileDrawer}"></button>
                    <div class="menu p-4 w-80 min-h-full bg-base-100" onclick="{handleDrawerClick}">

                        <!-- Main Navigation -->
                        <ul class="menu-compact">
                            <li>
                                <a href="#" onclick="{handleNavClick}" class="py-3 uppercase text-sm font-semibold {isActive('') || isActive('home') ? 'text-accent' : ''}">
                                    <i class="icon-metalark-logo text-lg"></i>
                                    <span>{locale?.t('nav_home')}</span>
                                </a>
                            </li>
                            <li>
                                <a href="#upload" onclick="{handleNavClick}" class="py-3 uppercase text-sm font-semibold {isActive('upload') ? 'text-accent' : ''}">
                                    <i class="icon-metalark-upload text-lg"></i>
                                    <span>{locale?.t('nav_upload')}</span>
                                </a>
                            </li>
                            <li>
                                <a href="#content" onclick="{handleNavClick}" class="py-3 uppercase text-sm font-semibold {isActive('content') ? 'text-accent' : ''}">
                                    <i class="icon-metalark-edit-note text-lg"></i>
                                    <span>{locale?.t('nav_content')}</span>
                                </a>
                            </li>
                            <li>
                                <a href="#skills" onclick="{handleNavClick}" class="py-3 uppercase text-sm font-semibold {isActive('skills') ? 'text-accent' : ''}">
                                    <i class="icon-metalark-skills text-lg"></i>
                                    <span>{locale?.t('nav_skills')}</span>
                                </a>
                            </li>
                            <li>
                                <a href="#reports" onclick="{handleNavClick}" class="py-3 uppercase text-sm font-semibold {isActive('reports') ? 'text-accent' : ''}">
                                    <i class="icon-metalark-chart text-lg"></i>
                                    <span>{locale?.t('nav_reports')}</span>
                                </a>
                            </li>
                            <li>
                                <a href="#export" onclick="{handleNavClick}" class="py-3 uppercase text-sm font-semibold {isActive('export') ? 'text-accent' : ''}">
                                    <i class="icon-metalark-export text-lg"></i>
                                    <span>{locale?.t('nav_metadata')}</span>
                                </a>
                            </li>
                        </ul>

                        <!-- Settings Divider -->
                        <div class="divider">{locale?.t('nav_settingsdivider')}</div>

                        <!-- Settings Menu -->
                        <ul class="menu-compact">
                            <li if="{state.isCustomerAdmin}">
                                <a href="#admin" onclick="{handleNavClick}" class="py-3 uppercase text-sm font-semibold {isAdminDashboardActive() ? 'text-accent' : ''}">
                                    <i class="icon-metalark-settings text-lg"></i>
                                    <span>{locale?.t('nav_admindashboard')}</span>
                                </a>
                            </li>
                            <!-- Mobile mirror of the workspace switcher in
                                 the desktop cog menu — multi-direct-role
                                 admins only. -->
                            <li if="{state.directRoleCount > 1}">
                                <a href="#/select-workspace" onclick="{handleNavClick}" class="py-3 uppercase text-sm font-semibold {isActive('select-workspace') ? 'text-accent' : ''}">
                                    <i class="icon-metalark-logo text-lg"></i>
                                    <span>{locale?.t('nav_selectworkspace')}</span>
                                </a>
                            </li>
                            <!-- Separator below the workspace switcher,
                                 mirroring the desktop cog menu. Hidden when
                                 the switcher itself is hidden. -->
                            <li if="{state.directRoleCount > 1}" class="pointer-events-none px-2">
                                <hr class="border-t border-base-200 my-1" />
                            </li>
                            <li>
                                <a href="#support" onclick="{handleNavClick}" class="py-3 uppercase text-sm font-semibold {isActive('support') ? 'text-accent' : ''}">
                                    <i class="icon-contact_support text-lg"></i>
                                    <span>{locale?.t('nav_support')}</span>
                                </a>
                            </li>
                            <li if="{state.isCustomerAdmin}">
                                <a href="#/customer-360?tab=billing&view=usage" onclick="{handleNavClick}" class="py-3 uppercase text-sm font-semibold {isCreditsNavActive() ? 'text-accent' : ''}">
                                    <i class="icon-metalark-list text-lg"></i>
                                    <span>{locale?.t('nav_processingcredits')}</span>
                                </a>
                            </li>
                            <li if="{state.isCustomerAdmin && state.featureFlags['billing_ui_page'] === true}">
                                <a href="#/customer-360?tab=billing&view=subscription" onclick="{handleNavClick}" class="py-3 uppercase text-sm font-semibold {isBillingNavActive() ? 'text-accent' : ''}">
                                    <i class="icon-metalark-cart text-lg"></i>
                                    <span>{locale?.t('nav_billing')}</span>
                                </a>
                            </li>
                            <li>
                                <a href="#settings" onclick="{handleNavClick}" class="py-3 uppercase text-sm font-semibold {isActive('settings') ? 'text-accent' : ''}">
                                    <i class="icon-metalark-settings text-lg"></i>
                                    <span>{locale?.t('nav_settings')}</span>
                                </a>
                            </li>
                            <li class="pointer-events-none px-2">
                                <hr class="border-t border-base-200 my-1" />
                            </li>
                            <li>
                                <a href="javascript:" onclick="{handleLogout}" class="py-3 uppercase text-sm font-semibold">
                                    <i class="icon-metalark-logout text-lg"></i>
                                    <span>{locale?.t('nav_logout')}</span>
                                </a>
                            </li>
                            <li class="bg-transparent py-3">
                                <language-selector class="flex flex-row"></language-selector>
                            </li>
                        </ul>

                    </div>
                </div>
            </div>
        </div>

    </div>

    <style>
        .main-menu i {
            font-size: 18px;
        }

        .menu li > *:active {
            background-color: transparent !important;
            color: inherit !important;
        }

        /*
         * Slide the workspace-menu cluster in from the right when it mounts
         * visible (admin enters a workspace) and out to the right when it
         * leaves (admin exits). Fold-out plays before the navbar's reload /
         * Redux cleanup by adding .exiting via JS and awaiting the
         * animation — the element would otherwise unmount instantly via
         * the <virtual if> wrapper before any transition could run.
         *
         * Short (300ms) and subtle (small translate + fade) so repeat plays
         * inside a session feel like a confirmation, not noise. Respects
         * prefers-reduced-motion so users who've asked their OS for less
         * motion get a plain show/hide.
         */
        .workspace-menu-cluster {
            animation: workspace-menu-slide-in 300ms ease-out;
            transform-origin: right center;
        }

        .workspace-menu-cluster.exiting {
            animation: workspace-menu-slide-out 300ms ease-in forwards;
            pointer-events: none;
        }

        @keyframes workspace-menu-slide-in {
            from {
                opacity: 0;
                transform: translateX(24px);
            }
            to {
                opacity: 1;
                transform: translateX(0);
            }
        }

        @keyframes workspace-menu-slide-out {
            from {
                opacity: 1;
                transform: translateX(0);
            }
            to {
                opacity: 0;
                transform: translateX(24px);
            }
        }

        @media (prefers-reduced-motion: reduce) {
            .workspace-menu-cluster,
            .workspace-menu-cluster.exiting {
                animation: none;
            }
        }
    </style>

    <script>
        import { getCurrentRoute, navigateTo } from "../router/routes"
        import { isPublicRoute } from "../config/routes.js"
        import { getAuthService, getFeatureFlagService } from "../services/service-loader";
        import languageSelector from "./language-selector.tag";

        export default {
            includeStore: true,

            getCurrentRoute,

            get components() {
                return {
                    languageSelector
                };
            },

            state: {
                isAdmin: false,
                isSystemAdmin: false,
                // CUSTOMER_ADMIN or higher. Separate from isAdmin (which also
                // includes TENANT_ADMIN) because customer-scoped nav — Admin
                // Dashboard, Processing Credits, Billing — all point at
                // Customer 360, which TENANT_ADMIN can't view.
                isCustomerAdmin: false,
                isPartnerAdmin: false, // METALARK-929: drives the partner "Administration" cog link
                isAuthenticated: false,
                userName: '',
                userRole: '',
                // Drives the "(Acting) ..." label swap — true only when the
                // active access session is IMPERSONATION kind. Workspace-access
                // sessions (admin's own direct role) are tracked separately.
                isImpersonating: false,
                // True during a direct-role workspace-access session. No banner,
                // no "(Acting)" label — the admin is using their first-class role.
                isWorkspaceAccess: false,
                // Workspace name populated during any active access session so
                // the header can show "Workspace: X" in place of the user/role line.
                workspaceName: '',
                // Direct-role-assignment count for admin-tier callers. Drives
                // workspace-menu visibility outside of an active session:
                //   0 direct roles → menu hidden (impersonate from customer-360)
                //   1 direct role  → menu visible; each click auto-creates
                //                    a wsa_ session for that single slice
                //   2+ direct roles → menu hidden; user must pick from the
                //                    Your Workspaces card on admin landing
                // Computed once during updateUserInfo; cached for the lifetime
                // of the navbar component (a session start/end already
                // reloads the page, which re-runs updateUserInfo).
                directRoleCount: 0,
                // Slice id + display name of the single direct-role slice
                // when directRoleCount === 1. Used by handleWorkspaceNavClick
                // to auto-create the session before navigating.
                directRoleSliceId: null,
                directRoleSliceName: '',
                directRoleCustomerId: '',
                directRoleCustomerName: '',
                // True when /auth/me returned a sliceId (single-native-workspace
                // bypass active or active access session). Tracked synchronously
                // in updateUserInfo so the workspace menu renders correctly on
                // first paint after a session exit/reload — without it the
                // menu briefly disappears while loadDirectRoleAssignments
                // resolves the slice count.
                hasImplicitWorkspaceContext: false,
                customerLogoUrl: null,
                customerName: '',
                partnerName: '', // METALARK-929: partner display name shown in the header for a not-entered PARTNER_ADMIN
                featureFlags: {},
                cacheBuster: Date.now(),
                // METALARK-835: pending-invitation count for the current
                // user. Drives the small dot on the settings cog and the
                // "Invitations" entry inside the cog dropdown. Sourced
                // from Redux's pendingInvitations slice — populated by
                // either the /invitations page or the accept-invitation
                // stale-link fallback, with a navbar-side fetch on mount
                // to seed it when neither has been visited yet.
                pendingInvitationCount: 0,
            },

            // hasActiveAccessSession spans both kinds. Use it in the template
            // to swap the header content (welcome/role line → workspace line)
            // regardless of which kind the admin is in.
            hasActiveAccessSession() {
                return this.state.isImpersonating || this.state.isWorkspaceAccess;
            },

            // Workspace-menu visibility rule. Callers see the menu when:
            //   - They're in an active access session (already inside a
            //     workspace context), OR
            //   - /auth/me returned a sliceId — single-native-workspace
            //     bypass (JWT stamped at login by resolveSessionSliceId),
            //     so they have implicit workspace context, OR
            //   - They have exactly one direct-role slice (admin-tier
            //     fallback: handleWorkspaceNavClick auto-creates the wsa_
            //     session on first menu click).
            //
            // Zero or 2+ direct roles WITHOUT implicit context means they
            // must first pick a workspace from the Your Workspaces card or
            // /select-workspace before the workspace nav becomes available.
            // The implicit-context check is synchronous (set in
            // updateUserInfo) so the menu renders correctly on first paint
            // after a reload — directRoleCount is filled in async by
            // loadDirectRoleAssignments and would otherwise leave the menu
            // briefly hidden.
            shouldShowWorkspaceMenu() {
                if (this.hasActiveAccessSession()) return true;
                if (this.state.hasImplicitWorkspaceContext) return true;
                return this.state.directRoleCount === 1;
            },

            closeMobileDrawer() {
                const cb = document.getElementById('nav-drawer');
                if (cb) cb.checked = false;
            },

            async onMounted() {
                console.log('[Navbar] Component mounted, setting up Redux subscription');

                // Always subscribe to Redux store changes to detect logout events
                // This is critical for detecting session expiration even on public pages
                let previousUserContext = this.store.getState().userContext;
                let previousCustomer = this.store.getState().customer;
                let previousCacheBuster = this.store.getState().cacheBuster;
                // Track the impersonation slice separately so the "(Acting) ..."
                // label updates when a session starts/ends — userContext alone
                // doesn't fire a re-render on impersonation-only state changes.
                let previousImpersonationToken = this.store.getState().impersonation?.token || null;
                // METALARK-835: track the pending-invitations cache so
                // the cog badge updates the instant an accept / decline
                // / resend lands without waiting for a navbar remount.
                let previousPendingInvitations = this.store.getState().pendingInvitations;
                console.log('[Navbar onMounted] Initial state:', {
                    userContext: previousUserContext ? 'exists' : 'NULL',
                    customer: previousCustomer ? 'exists' : 'NULL',
                    cacheBuster: previousCacheBuster
                });

                // Subscribe to Redux store changes for userContext, customer, and cacheBuster
                 // This ensures navbar updates when user logs in/out, customer changes, or cache needs busting
                 // IMPORTANT: This subscription must happen even on public pages to detect session expiration
                 this.unsubscribe = this.store.subscribe(async () => {
                     const currentUserContext = this.store.getState().userContext;
                     const currentCustomer = this.store.getState().customer;

                     const currentCacheBuster = this.store.getState().cacheBuster;

                     console.log('[Navbar Redux Subscription] State change detected:', {
                         userContextChanged: currentUserContext !== previousUserContext,
                         customerChanged: currentCustomer !== previousCustomer,
                         cacheBusterChanged: currentCacheBuster !== previousCacheBuster,
                         currentUserContext: currentUserContext ? 'exists' : 'NULL',
                         previousUserContext: previousUserContext ? 'exists' : 'NULL'
                     });

                     // Update if userContext changed (including when it becomes null after logout/session expiration)
                     if (currentUserContext !== previousUserContext) {
                         console.log('[Navbar] User context changed, updating navbar');
                         previousUserContext = currentUserContext;

                         // Check if this is an intentional logout (flag set before Redux clear)
                         // This prevents /auth/me API calls during manual logout
                         const isIntentionalLogout = sessionStorage.getItem('intentional_logout') === 'true';

                         if (currentUserContext === null && isIntentionalLogout) {
                             console.log('[Navbar] Intentional logout detected, skipping updateUserInfo to prevent /auth/me');
                             sessionStorage.removeItem('intentional_logout'); // Clear flag

                             // Clear navbar state to hide navbar and user info
                             this.state.isAuthenticated = false;
                             this.state.userName = '';
                             this.state.userRole = '';
                             this.state.isAdmin = false;
                             this.state.isSystemAdmin = false;
                             this.state.customerLogoUrl = null;
                             this.state.customerName = '';

                             this.update(); // Update to clear navbar display
                             return;
                         }

                         await this.updateUserInfo();
                     }

                     // Update if customer changed
                     if (currentCustomer !== previousCustomer) {
                         console.log('[Navbar] Customer data changed, updating navbar');
                         previousCustomer = currentCustomer;
                         this.updateCustomerFromStore();
                     }

                     // Update if cache buster changed
                     if (currentCacheBuster !== previousCacheBuster) {
                         console.log('[Navbar] Cache buster changed, updating logo');
                         previousCacheBuster = currentCacheBuster;
                         this.state.cacheBuster = currentCacheBuster;
                         this.update();
                     }

                     // Update on access-session start/end so the header swaps
                     // immediately without waiting for a /auth/me round-trip.
                     // Covers both kinds — imp_ (impersonation, "(Acting)" label)
                     // and wsa_ (workspace-access, customer/workspace header) —
                     // using the same Redux slice since backend stores them in
                     // one table.
                     const currentImpersonationToken = this.store.getState().impersonation?.token || null;
                     if (currentImpersonationToken !== previousImpersonationToken) {
                         previousImpersonationToken = currentImpersonationToken;
                         this.applyAccessSessionState();
                         this.update();
                     }

                     // METALARK-835: update the cog badge + dropdown
                     // count when the pending-invitations cache changes
                     // (accept, decline, resend invalidations all flow
                     // through Redux from userService).
                     const currentPendingInvitations = this.store.getState().pendingInvitations;
                     if (currentPendingInvitations !== previousPendingInvitations) {
                         previousPendingInvitations = currentPendingInvitations;
                         const nextCount = currentPendingInvitations?.invitations?.length ?? 0;
                         if (nextCount !== this.state.pendingInvitationCount) {
                             this.update({ pendingInvitationCount: nextCount });
                         }
                     }
                 });
                
                // Check if we're on a public page using centralized config
                const hash = window.location.hash || ''
                const isPublicPage = isPublicRoute(hash)

                if (isPublicPage) {
                    console.log('[Navbar] On public page, skipping user info load but subscription is active')
                    return
                }

                // Load initial user context and customer data from store (only on protected pages)
                await this.updateUserInfo();
                this.updateCustomerFromStore();
                await this.loadFeatureFlags();

                // Initialize cache buster from store
                this.state.cacheBuster = store.getState().cacheBuster;
                this.update();

                // METALARK-835: seed pending-invitations count. Uses
                // the sessionStorage-backed Redux cache when present
                // so page reloads pick up the badge without a network
                // round-trip; falls through to a single /mine fetch
                // on cold start (fresh sign-in, empty sessionStorage)
                // so the badge is correct from the first authenticated
                // page render.
                //
                // Done best-effort — a 401 / network failure leaves
                // the count at 0 and the badge stays hidden, which
                // is the right failure mode for a notification UI.
                this.loadPendingInvitationCount();

                this._handleGearMenuOutsideClick = (e) => {
                    const gearMenu = document.getElementById('gear-menu');
                    if (gearMenu?.open && !gearMenu.contains(e.target)) {
                        gearMenu.open = false;
                    }
                };
                document.addEventListener('click', this._handleGearMenuOutsideClick);
            },

            // Cache-gated fetch (METALARK-835). When sessionStorage
            // already holds a userUuid-matching cache from this tab's
            // earlier work, userService short-circuits and no /mine
            // request fires — reload-after-reload of /home is silent.
            // First-visit-after-login is the only path that hits the
            // network from the navbar; the dedicated invitation pages
            // refresh on their own mount independently.
            async loadPendingInvitationCount() {
                try {
                    const { getUserService } = await import('../services/service-loader');
                    const userService = await getUserService();
                    const result = await userService.listMyInvitations({
                        useCache: true,
                        skipLoading: true,
                    });
                    const count = result?.invitations?.length ?? 0;
                    if (count !== this.state.pendingInvitationCount) {
                        this.update({ pendingInvitationCount: count });
                    }
                } catch (err) {
                    console.debug('[Navbar] Failed to load pending invitations (non-fatal):', err?.message);
                }
            },
            onUnmounted() {
                // Clean up subscription
                console.log('[Navbar] Component unmounting, cleaning up subscription');
                if (this.unsubscribe) {
                    this.unsubscribe();
                }
                if (this._handleGearMenuOutsideClick) {
                    document.removeEventListener('click', this._handleGearMenuOutsideClick);
                }
            },
            
            // METALARK-929: resolve the partner's display name for the header.
            // A not-entered PARTNER_ADMIN reads its own partner (partners:read).
            async loadPartnerName(partnerId) {
                try {
                    const { getPartnerService } = await import('../services/service-loader');
                    const partnerService = await getPartnerService();
                    const partner = await partnerService.getById(partnerId);
                    this.state.partnerName = partner?.displayName || partner?.data?.displayName || '';
                    this.update();
                } catch (e) {
                    console.warn('[Navbar] Failed to load partner name:', e);
                }
            },

            async updateUserInfo() {
                console.log('[Navbar] updateUserInfo called');
                const userContext = await this.tools.getUserContext();
                console.log('[Navbar] User context:', userContext);

                // Check if user is authenticated (userId !== null means real user, not guest)
                this.state.isAuthenticated = userContext && userContext.userId !== null;
                console.log('[Navbar] isAuthenticated:', this.state.isAuthenticated);

                if (this.state.isAuthenticated) {
                    // Store raw user name (translation will happen in template)
                    this.state.userName = userContext.name ||
                        (userContext.givenName && userContext.familyName
                            ? `${userContext.givenName} ${userContext.familyName}`
                            : userContext.email);

                    // Store raw role (translation will happen in template)
                    this.state.userRole = userContext.role || 'TENANT_USER';

                    // Sync implicit workspace context flag from /auth/me. A
                    // populated sliceId means the JWT was stamped by the
                    // single-native-workspace bypass (or an active session
                    // is targeting that slice) — either way, the workspace
                    // menu should be visible immediately rather than waiting
                    // for the async direct-role count to resolve.
                    this.state.hasImplicitWorkspaceContext = !!userContext.sliceId;

                    // Set access-session state (kind-aware: impersonation vs
                    // workspace-access) from Redux + /auth/me. Prefer explicit
                    // kind if present; otherwise fall back to token-prefix.
                    this.applyAccessSessionState(userContext);

                    // Check if user has admin permissions - do them sequentially
                    console.log('[Navbar] Calling tools.isAdmin()...');
                    const isAdmin = await this.tools.isAdmin();
                    console.log('[Navbar] tools.isAdmin() returned:', isAdmin);
                    this.state.isAdmin = isAdmin;

                    console.log('[Navbar] Calling tools.hasRole(SYSTEM_ADMIN)...');
                    const isSystemAdmin = await this.tools.hasRole('SYSTEM_ADMIN');
                    console.log('[Navbar] tools.hasRole(SYSTEM_ADMIN) returned:', isSystemAdmin);
                    this.state.isSystemAdmin = isSystemAdmin;

                    this.state.isCustomerAdmin = await this.tools.hasAnyRole(['SYSTEM_ADMIN', 'CUSTOMER_ADMIN']);
                    this.state.isPartnerAdmin = userContext.role === 'PARTNER_ADMIN'; // METALARK-929
                    // METALARK-929: a PARTNER_ADMIN before entering a customer has no
                    // customer context — resolve its partner name for the header.
                    // Fire-and-forget; the header renders without it until it lands.
                    if (this.state.isPartnerAdmin && userContext.partnerId) {
                        this.loadPartnerName(userContext.partnerId);
                    } else {
                        this.state.partnerName = '';
                    }

                    // Load the admin's direct-role slice count so the workspace
                    // menu can be hidden (0 or 2+) or shown with auto-enter
                    // (exactly 1). Non-admins skip this inside the helper.
                    // Fire-and-forget — the navbar still renders correctly
                    // while the call is in flight because shouldShowWorkspaceMenu
                    // defaults to hidden-for-admin until the count lands, and
                    // the subsequent update() call re-renders with the resolved
                    // menu visibility.
                    this.loadDirectRoleAssignments().then(() => this.update());

                    console.log('[Navbar] Final state:', {
                        isAdmin: this.state.isAdmin,
                        isSystemAdmin: this.state.isSystemAdmin,
                        role: userContext.role,
                        userName: this.state.userName
                    });
                } else {
                    // Clear user info when logged out
                    this.state.userName = '';
                    this.state.userRole = '';
                    this.state.isImpersonating = false;
                    this.state.isWorkspaceAccess = false;
                    this.state.workspaceName = '';
                    this.state.isAdmin = false;
                    this.state.isSystemAdmin = false;
                    this.state.isCustomerAdmin = false;
                    this.state.isPartnerAdmin = false;
                    this.state.hasImplicitWorkspaceContext = false;
                    this.state.directRoleCount = 0;
                    this.state.customerLogoUrl = null;
                    this.state.customerName = '';
                    this.state.partnerName = '';
                    console.log('[Navbar] User not authenticated, cleared admin flags');
                }

                // Force update to ensure Riot detects the state changes
                console.log('[Navbar] Calling this.update()');
                this.update();
            },

            // Build the role label the header shows. During IMPERSONATION the
            // backend already returns the effective (downgraded) role via
            // MultiTenantAuthMiddleware.getImpersonationEffectiveRole, so we
            // just pick the matching "(Acting) ..." translation. Workspace-access
            // sessions never use "(Acting) ..." — those are first-class roles,
            // and the header switches to a Customer/Workspace view instead of
            // showing user/role at all (see hasActiveAccessSession in template).
            getDisplayRole() {
                const role = this.state.userRole;
                if (!role || !this.locale) return '';
                if (this.state.isImpersonating) {
                    const actingKey = {
                        CUSTOMER_ADMIN: 'role_acting_customer_admin',
                        TENANT_ADMIN: 'role_acting_tenant_admin',
                    }[role];
                    if (actingKey) return this.locale.t(actingKey);
                }
                return this.locale.t(role);
            },

            // Load the caller's direct-role slice assignments. Drives the
            // workspace-menu visibility + single-slice auto-entry for
            // admin-tier users AND the Select Workspace cog menu entry
            // visibility for any role with two or more direct workspace
            // roles. The single-direct-role bypass (auto-create wsa_
            // session via handleWorkspaceNavClick) stays admin-only —
            // non-admin tiers carry their sliceId on the JWT directly,
            // so they don't need a wsa_ session — but the count itself
            // is computed for everyone so the workspace switcher entry
            // surfaces consistently across roles.
            // Failures don't block navbar rendering — fall through with
            // a zero count, which hides the multi-workspace switcher and
            // forces the admin to the Your Workspaces card (same UX as
            // the zero-direct-role path).
            async loadDirectRoleAssignments() {
                const role = this.state.userRole;
                if (!role) {
                    this.state.directRoleCount = 0;
                    this.state.directRoleSliceId = null;
                    this.state.directRoleSliceName = '';
                    this.state.directRoleCustomerId = '';
                    this.state.directRoleCustomerName = '';
                    return;
                }

                try {
                    const userContext = await this.tools.getUserContext();
                    const userUuid = userContext?.userUuid;
                    if (!userUuid) return;

                    const { getUserService, getCustomerService, getSliceService } = await import('../services/service-loader');
                    const userService = await getUserService();
                    const userWithRoles = await userService.getUserWithRoles(userUuid);

                    // Collapse to distinct sliceIds. Counting raw role rows
                    // double-counted users with multiple role assignments on
                    // the same slice (e.g. a CUSTOMER_ADMIN tier user who
                    // also holds a slice-scoped TENANT_ADMIN row, or
                    // duplicated rows in legacy data). Filtering by
                    // `r.role === userRole` was the prior mitigation but
                    // it dropped the count to zero whenever the caller's
                    // primary tier differed from their slice-scoped role
                    // (e.g. CUSTOMER_ADMIN with a TENANT_ADMIN slice
                    // assignment), which hid the workspace menu and
                    // workspace switcher. Distinct sliceId reflects "how
                    // many workspaces does the caller have direct access
                    // to" — the actual signal both UI rules want.
                    const sliceIdToRole = new Map();
                    for (const r of userWithRoles?.roles || []) {
                        if (r?.sliceId && !sliceIdToRole.has(r.sliceId)) {
                            sliceIdToRole.set(r.sliceId, r);
                        }
                    }
                    const directRoles = Array.from(sliceIdToRole.values());

                    this.state.directRoleCount = directRoles.length;

                    if (directRoles.length === 1) {
                        // Resolve display names so handleWorkspaceNavClick can
                        // populate the Redux impersonation slice with the
                        // right labels for the navbar header swap without a
                        // second roundtrip mid-click.
                        const only = directRoles[0];
                        this.state.directRoleSliceId = only.sliceId;
                        this.state.directRoleCustomerId = only.customerId;
                        try {
                            const sliceService = await getSliceService();
                            const slice = await sliceService.getById(only.sliceId);
                            this.state.directRoleSliceName = slice?.displayName || only.sliceId;
                        } catch {
                            this.state.directRoleSliceName = only.sliceId;
                        }
                        try {
                            const customerService = await getCustomerService();
                            const customer = await customerService.getById(only.customerId);
                            this.state.directRoleCustomerName = customer?.displayName || only.customerId;
                        } catch {
                            this.state.directRoleCustomerName = only.customerId;
                        }
                    } else {
                        this.state.directRoleSliceId = null;
                        this.state.directRoleSliceName = '';
                        this.state.directRoleCustomerId = '';
                        this.state.directRoleCustomerName = '';
                    }
                } catch (error) {
                    console.warn('[Navbar] Failed to load direct-role assignments; workspace menu will be hidden for admin-tier callers:', error);
                    this.state.directRoleCount = 0;
                }
            },

            // Apply .exiting to the workspace-menu cluster and wait for the
            // slide-out animation (see <style scoped>). Resolves immediately
            // when the cluster isn't mounted or when prefers-reduced-motion
            // is set, so the calling flow isn't delayed unnecessarily.
            // Called before reload-based exits (settings cog) so the user
            // sees the cluster fold out before the admin view replaces it.
            async animateWorkspaceMenuExit() {
                try {
                    const cluster = this.root?.querySelector?.('.workspace-menu-cluster')
                        || document.querySelector('.workspace-menu-cluster');
                    if (!cluster) return;
                    const reduced = typeof window !== 'undefined'
                        && window.matchMedia
                        && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
                    if (reduced) return;
                    cluster.classList.add('exiting');
                    await new Promise(resolve => setTimeout(resolve, 300));
                } catch (error) {
                    console.warn('[Navbar] Workspace-menu exit animation failed; continuing exit flow:', error);
                }
            },

            // Intercepts workspace-nav clicks for the single-direct-role case:
            // if the admin has exactly one direct-role slice and isn't in an
            // active session, start a workspace-access session for that slice
            // first, then land on the requested route. All other cases
            // (active session, no direct roles, 2+ direct roles, non-admin)
            // fall through to the normal handleNavClick cache-clear + default
            // link behavior.
            //
            // METALARK-738: Single-native-workspace admins now have their
            // sliceId stamped on the auth_token JWT at login (see backend
            // resolveSessionSliceId). For them, the workspace IS already the
            // session's implicit context — no wsa_ session, no reload.
            // userContext.sliceId being populated is the signal that the
            // bypass applies; skip the auto-enter ceremony and let the
            // default <a href> SPA navigation through.
            async handleWorkspaceNavClick(event, targetHash) {
                let userContext = null;
                try {
                    userContext = await this.tools.getUserContext();
                } catch {
                    // Non-fatal — fall through; the auto-enter logic is the
                    // safe default for admins without a resolved sliceId.
                }
                const hasImplicitWorkspaceContext = !!userContext?.sliceId;

                const needsAutoEnter = !this.hasActiveAccessSession()
                    && this.state.directRoleCount === 1
                    && this.state.directRoleSliceId
                    && !hasImplicitWorkspaceContext;

                if (!needsAutoEnter) {
                    // The workspace-nav links use href="#" as a placeholder
                    // (the original auto-enter flow set the hash itself), so
                    // the default link behavior would clear the hash to ''
                    // and bounce admin-tier callers back to the admin
                    // landing via the routes.js implicit-route guard.
                    // Suppress the default and navigate to the intended
                    // hash explicitly. handleNavClick still runs to clear
                    // back-nav + per-page caches.
                    if (event?.preventDefault) event.preventDefault();
                    this.handleNavClick();
                    if (targetHash) {
                        window.location.hash = targetHash;
                    }
                    return;
                }

                // Auto-enter flow: suppress the default link navigation, start
                // the session, then set the hash and reload. Reload matches
                // the my-workspaces-card enter flow so caches primed by the
                // admin-tier view are flushed.
                if (event?.preventDefault) event.preventDefault();
                try {
                    const { getImpersonationService } = await import('../services/service-loader');
                    const impersonationService = await getImpersonationService();

                    // Check for an existing wsa_ session for this slice first
                    // so re-entry via the navbar reuses rather than creating
                    // a duplicate — mirrors the card's reuse path.
                    let existingToken = null;
                    try {
                        const sessionsResp = await impersonationService.getWorkspaceAccessSessions();
                        const match = (sessionsResp?.sessions || [])
                            .find(s => s?.targetSliceId === this.state.directRoleSliceId);
                        if (match?.token) existingToken = match.token;
                    } catch {
                        // Non-fatal — fall through to create a new session.
                    }

                    if (existingToken) {
                        localStorage.setItem('authToken', existingToken);
                        this.store.dispatch({
                            type: 'setImpersonation',
                            payload: {
                                token: existingToken,
                                kind: 'WORKSPACE_ACCESS',
                                targetSliceId: this.state.directRoleSliceId,
                                targetSliceName: this.state.directRoleSliceName,
                                targetCustomerId: this.state.directRoleCustomerId,
                                targetCustomerName: this.state.directRoleCustomerName,
                            },
                        });
                    } else {
                        const result = await impersonationService.beginWorkspaceAccess(this.state.directRoleSliceId);
                        if (result?.token) {
                            localStorage.setItem('authToken', result.token);
                        }
                        const ctx = result?.context || {};
                        this.store.dispatch({
                            type: 'setImpersonation',
                            payload: {
                                token: result?.token,
                                kind: 'WORKSPACE_ACCESS',
                                targetSliceId: ctx.targetSliceId || this.state.directRoleSliceId,
                                targetSliceName: ctx.targetSliceName || this.state.directRoleSliceName,
                                targetCustomerId: ctx.targetCustomerId || this.state.directRoleCustomerId,
                                targetCustomerName: ctx.targetCustomerName || this.state.directRoleCustomerName,
                                expiresAt: ctx.expiresAt,
                            },
                        });
                    }

                    window.location.hash = targetHash;
                    window.location.reload();
                } catch (error) {
                    console.error('[Navbar] Auto-enter workspace failed:', error);
                    // Let the default link behavior happen as a fallback —
                    // it'll 404 or 401 on the backend but won't leave the
                    // user stuck on a broken-looking navbar click.
                    window.location.hash = targetHash;
                }
            },

            // Resolve the active access session from Redux + optional
            // userContext. Redux is authoritative for token / expiresAt / kind /
            // target-slice name, since it is populated by the begin-session
            // response and persisted via seeding at store init. userContext is
            // a secondary signal — primarily for the `impersonating` flag that
            // /auth/me returns from the backend query context.
            applyAccessSessionState(userContext) {
                const reduxImpersonation = this.store.getState().impersonation;
                const hasActive = !!(reduxImpersonation && reduxImpersonation.expiresAt);

                // Resolve kind: explicit field wins; otherwise derive from
                // token prefix (wsa_ / imp_). This keeps the navbar correct
                // even if the begin-session payload predates the kind field
                // (early load / cached payload).
                let kind = null;
                if (hasActive) {
                    kind = reduxImpersonation.kind
                        || (reduxImpersonation.token?.startsWith('wsa_') ? 'WORKSPACE_ACCESS' : 'IMPERSONATION');
                }

                this.state.isImpersonating = kind === 'IMPERSONATION'
                    || (!!userContext?.impersonating && kind !== 'WORKSPACE_ACCESS');
                this.state.isWorkspaceAccess = kind === 'WORKSPACE_ACCESS';
                this.state.workspaceName = (hasActive && reduxImpersonation.targetSliceName) || '';
            },

            updateCustomerFromStore() {
                console.log('[Navbar] updateCustomerFromStore called');
                const customer = this.store.getState().customer;

                if (customer) {
                    console.log('[Navbar] Customer data from store:', customer);
                    this.state.customerName = customer.name || '';
                    this.state.customerLogoUrl = customer.logoUrl || null;
                } else {
                    console.log('[Navbar] No customer data in store');
                    this.state.customerName = '';
                    this.state.customerLogoUrl = null;
                }

                this.update();
            },

            async loadFeatureFlags() {
                try {
                    const featureFlagService = await getFeatureFlagService();
                    const response = await featureFlagService.getActiveFlags();
                    const flags = response.flags;
                    this.state.featureFlags = flags;
                    console.log('[Navbar] Loaded feature flags:', flags);
                } catch (error) {
                    console.error('[Navbar] Failed to load feature flags:', error);
                    this.state.featureFlags = {};
                }
                this.update();
            },

            getRoleLabel(role) {
                // Use locale service to translate role labels
                return this.locale.t(role) || this.locale.t('TENANT_USER');
            },

            isActive(name) {
                if (!name) return !getCurrentRoute();
                const route = getCurrentRoute().split('?')[0];

                return route.indexOf(name) > -1;
            },

            // Processing Credits link goes deep into Customer 360 — only
            // highlight when the user is actually viewing the credits
            // section, not any other customer-360 tab.
            isCreditsNavActive() {
                const route = getCurrentRoute() || '';
                if (!route.includes('customer-360')) return false;
                const qs = route.split('?')[1] || '';
                const params = new URLSearchParams(qs);
                // Credits balance + bundles now live at the top of the
                // Usage pill. Purchase Credits is its own pill. Treat
                // both as "Processing Credits" for nav-highlight purposes.
                return params.get('tab') === 'billing'
                    && (params.get('view') === 'usage' || params.get('view') === 'purchase');
            },

            // Billing nav points at the Subscription pill (plan state +
            // event history + invoices). Complements isCreditsNavActive,
            // which owns Usage/Purchase — the two nav links never
            // highlight at the same time.
            isBillingNavActive() {
                const route = getCurrentRoute() || '';
                if (!route.includes('customer-360')) return false;
                const qs = route.split('?')[1] || '';
                const params = new URLSearchParams(qs);
                return params.get('tab') === 'billing' && params.get('view') === 'subscription';
            },

            // Admin Dashboard highlight. For CUSTOMER_ADMIN, the router
            // redirects #/admin → #/customer-360, so their "admin dashboard"
            // IS the Customer 360 page. Treat any customer-360 URL as an
            // active match for them — but let more specific child links
            // (Processing Credits, Billing) win so two nav items don't
            // highlight at once.
            isAdminDashboardActive() {
                if (this.isActive('admin')) return true;
                if (this.state.userRole === 'CUSTOMER_ADMIN' && this.isActive('customer-360')) {
                    return !this.isCreditsNavActive() && !this.isBillingNavActive();
                }
                return false;
            },

            handleNavClick() {
                // Clear back navigation flags for explicit navbar navigation
                // Use setTimeout to ensure flags are cleared AFTER any popstate events
                setTimeout(() => {
                    this.tools.clearBackNavigationFlags();
                    // Also clear all saved page states for fresh page loads
                    this.tools.clearAllPageStates();
                }, 0);
                // Close dropdown if open
                this.closeDropdown();
            },

            closeDropdown() {
                const gearMenu = document.getElementById('gear-menu');
                if (gearMenu) gearMenu.open = false;
            },

            handleDrawerClick(e) {
                // Check if a link was clicked (not a label or close button)
                const link = e.target.closest('a');
                if (link && link.getAttribute('href') && !link.getAttribute('href').startsWith('javascript:')) {
                    // Close the drawer by unchecking the checkbox
                    const drawerCheckbox = document.getElementById('nav-drawer');
                    if (drawerCheckbox) {
                        drawerCheckbox.checked = false;
                    }
                }
            },

            handleLogoError(e) {
                console.warn('[Navbar] Failed to load customer logo, falling back to customer name.');
                this.state.customerLogoUrl = null;
                this.update();
            },

            // Exits the current access session from the settings-cog dropdown
            // and routes back to the caller's admin landing page.
            //
            // Both kinds PAUSE — the server session stays alive so the admin
            // can resume it later (from the impersonation Active Sessions
            // card for imp_, or from the Your Workspaces card for wsa_).
            // Only local token + Redux are cleared. Mirrors the impersonation
            // banner's returnToAdmin() flow. Explicit END is available from
            // the Active Sessions card for both kinds when the admin wants
            // to cleanly terminate a session rather than keep it warm.
            //
            // SYSTEM_ADMIN lands on #customers, CUSTOMER_ADMIN on their
            // Customer 360 — matches the post-login landing in routes.js §1.5.
            async handleExitAccessSession() {
                try {
                    this.closeDropdown();

                    // Play the fold-up exit animation before the reload so
                    // the cluster slides out visibly rather than vanishing
                    // when the reload clears the DOM. Skipped when the
                    // browser has prefers-reduced-motion or the cluster
                    // isn't currently mounted (nothing to animate).
                    await this.animateWorkspaceMenuExit();

                    // Local cleanup — same for both kinds. Drop the Bearer +
                    // content-access JWT + Redux slice. Server-side session
                    // remains in place for later resume.
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('workspace_sliceId');
                    localStorage.removeItem('workspace_sliceName');
                    document.cookie = 'launch_token=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax';
                    this.store.dispatch({ type: 'clearImpersonation' });
                    this.store.dispatch({ type: 'setSliceId', payload: null });

                    // Route back to the admin landing page. Pull the pre-session
                    // role directly from userContext — the current session's
                    // effectiveRole may be downgraded (impersonation case),
                    // but userContext.role tracks the caller's own tier.
                    const userContext = await this.tools.getUserContext();
                    const role = userContext?.role;
                    if (role === 'SYSTEM_ADMIN') {
                        window.location.hash = '#customers';
                    } else if (role === 'CUSTOMER_ADMIN' && userContext?.customerId) {
                        window.location.hash = `#/customer-360?customerId=${encodeURIComponent(userContext.customerId)}`;
                    } else {
                        window.location.hash = '#home';
                    }
                    // Full reload flushes caches that the session's
                    // effective role primed (sessionCache, feature-flag cache,
                    // workspace store). Same reason the impersonation banner
                    // reloads on exit.
                    window.location.reload();
                } catch (error) {
                    console.error('[Navbar] Failed to exit access session:', error);
                }
            },

            async handleLogout() {
                try {
                    // Close dropdown first
                    this.closeDropdown();

                    // Mark that we're intentionally logging out (before Redux is cleared)
                    // This flag prevents the navbar subscription from triggering /auth/me calls
                    sessionStorage.setItem('intentional_logout', 'true');
                    console.log('[Navbar] Set intentional_logout flag');

                    // Call backend logout API
                    const authService = await getAuthService();
                    await authService.logout();

                    // Clear all authentication state
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('workspace_sliceId');
                    localStorage.removeItem('workspace_sliceName');
                    localStorage.removeItem('last_selected_slice_id');
                    sessionStorage.removeItem('workspace_auto_select_attempted');

                    // Clear Redux store and user context
                    this.store.dispatch({ type: 'clearImpersonation' });
                    this.store.dispatch({ type: 'setSliceId', payload: null });
                    this.store.dispatch({ type: 'CLEAR_CUSTOMER' });
                    await this.tools.clearUserContext();

                    // Navigate to login page using hash navigation
                    // The router and login page will handle showing the login form
                    navigateTo('login');
                } catch (error) {
                    console.error('Logout error:', error);
                    // Even if logout API fails, clear local state and redirect
                    localStorage.removeItem('authToken');
                    localStorage.removeItem('workspace_sliceId');
                    localStorage.removeItem('workspace_sliceName');
                    localStorage.removeItem('last_selected_slice_id');
                    sessionStorage.removeItem('workspace_auto_select_attempted');
                    this.store.dispatch({ type: 'clearImpersonation' });
                    this.store.dispatch({ type: 'setSliceId', payload: null });
                    this.store.dispatch({ type: 'CLEAR_CUSTOMER' });
                    await this.tools.clearUserContext();
                    navigateTo('login');
                }
            }
        }
    </script>
</navbar>